import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, Feather } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { WanderButton } from "@/components/WanderButton";
import { SquareLogo } from "@/components/ornaments";
import { SearchBox } from "@/components/SearchBox";
import { SpoilerShieldButton } from "@/components/SpoilerShield";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useT } from "@/lib/labels";
import { CustomLink, useCustomButtons } from "@/components/CustomContent";

const navCls = "font-heading text-[12px] font-bold uppercase tracking-[0.2em] px-3 py-2 text-[#2d1b0e] hover:text-[#991b1b] transition-colors inline-flex items-center gap-1";

const Menu2 = ({ label, items, testid, extra }) => (
  <DropdownMenu>
    <DropdownMenuTrigger data-testid={testid} className={navCls}>{label} <ChevronDown className="w-3 h-3" /></DropdownMenuTrigger>
    <DropdownMenuContent className="vellum rounded-none min-w-[15rem] p-1">
      {items.map((i) => (
        <DropdownMenuItem key={i.to} asChild className="font-heading text-sm cursor-pointer rounded-none focus:bg-[#c9a227]/20">
          <Link to={i.to} data-testid={`section-nav-link-${i.key}`}>{i.label}</Link>
        </DropdownMenuItem>
      ))}
      {extra && <><DropdownMenuSeparator className="bg-[#c9a227]/40" />{extra}</>}
    </DropdownMenuContent>
  </DropdownMenu>
);

const MoreMenu = ({ label }) => {
  const items = useCustomButtons("nav");
  if (!items.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-more" className={navCls}>{label} <ChevronDown className="w-3 h-3" /></DropdownMenuTrigger>
      <DropdownMenuContent className="vellum rounded-none min-w-[15rem] p-1">
        {items.map((b) => (
          <DropdownMenuItem key={b.id} asChild className="font-heading text-sm cursor-pointer rounded-none focus:bg-[#c9a227]/20">
            <CustomLink href={b.href} testid={`custom-btn-nav-${b.id}`} className="flex items-center gap-1">{b.text}</CustomLink>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const useNavItems = () => {
  const { sections, series, settings } = useSite();
  return {
    settings,
    seriesItems: series.map((s) => ({ key: s.slug, to: `/series/${s.slug}`, label: s.title })),
    sectionItems: sections.filter((s) => s.show_in_nav).map((s) => ({ key: s.slug, to: `/section/${s.slug}`, label: s.name })),
  };
};

const MobileNav = () => {
  const { seriesItems, sectionItems, settings } = useNavItems();
  const { user } = useAuth();
  const t = useT();
  const [open, setOpen] = useState(false);
  const all = [...seriesItems, ...sectionItems, { key: "map", to: "/map", label: t("mobile.map") }, { key: "codex", to: "/codex", label: t("mobile.all_pages") }];
  if (settings.vault_enabled) all.push({ key: "vault", to: "/vault", label: t("mobile.vault") });
  all.push(user?.role === "admin" ? { key: "study", to: "/admin", label: t("mobile.study") } : user ? { key: "scrolls", to: "/contribute", label: t("mobile.scrolls") } : settings.readers_enabled ? { key: "join", to: "/join?tab=signin", label: t("mobile.join") } : { key: "join", to: "", label: "" });
  if (user && user.profile_slug) all.push({ key: "profile", to: `/reader/${user.profile_slug}`, label: t("mobile.profile") });
  const custom = useCustomButtons("nav");
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger data-testid="mobile-menu-button" className="lg:hidden p-2 border border-[#c9a227]"><Menu className="w-5 h-5" /></SheetTrigger>
      <SheetContent className="bg-[#f4efe2] border-l-2 border-[#c9a227] overflow-y-auto">
        <SheetTitle className="font-display text-2xl mb-6">{t("mobile.title")}</SheetTitle>
        <SearchBox testid="mobile-search" className="mb-4" />
        {t("mobile.wander") && <div onClick={() => setOpen(false)} className="mb-4"><WanderButton testid="mobile-wander-button" label={t("mobile.wander")} className="btn-ghost-illum w-full justify-center" /></div>}
        <nav className="flex flex-col gap-1">
          {all.filter((i) => i.label).map((i) => <Link key={i.to} to={i.to} onClick={() => setOpen(false)} className="font-heading py-2 border-b border-[#c9a227]/30">{i.label}</Link>)}
          {custom.map((b) => <CustomLink key={b.id} href={b.href} testid={`mobile-custom-${b.id}`} onClick={() => setOpen(false)} className="font-heading py-2 border-b border-[#c9a227]/30 flex items-center gap-1">{b.text}</CustomLink>)}
        </nav>
      </SheetContent>
    </Sheet>
  );
};

const AccountLink = () => {
  const { user } = useAuth();
  const { settings } = useSite();
  const t = useT();
  if (!user) {
    if (!settings.readers_enabled) return null;
    return <Link to="/join" data-testid="header-reader-login" className={`${navCls} !text-[#fdf6e3] bg-[#991b1b] border border-[#c9a227] hover:!bg-[#ea580c] whitespace-nowrap max-sm:whitespace-normal max-sm:text-[10px] max-sm:tracking-[0.12em] max-sm:leading-tight max-sm:px-2 max-sm:py-1.5 max-sm:max-w-[9.5rem] max-sm:text-center`}><Feather className="w-3.5 h-3.5" /> {t("nav.reader_contribution")}</Link>;
  }
  const to = user.role === "admin" ? "/admin" : "/contribute";
  const label = user.role === "admin" ? t("nav.study") : t("nav.my_scrolls");
  return <Link to={to} data-testid="header-account-link" className={`${navCls} border border-[#c9a227]/60 whitespace-nowrap max-sm:whitespace-normal max-sm:text-[10px] max-sm:tracking-[0.12em] max-sm:leading-tight max-sm:px-2 max-sm:py-1.5 max-sm:max-w-[9.5rem] max-sm:text-center`}><Feather className="w-3.5 h-3.5" /> {label}</Link>;
};

export const Header = () => {
  const { seriesItems, sectionItems, settings } = useNavItems();
  const t = useT();
  return (
    <header data-testid="site-header" className="sticky top-0 z-40 header-vellum">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-20 flex items-center gap-2 sm:gap-3">
        <Link to="/" data-testid="header-home-link" className="flex items-center gap-3 shrink-0 group">
          <SquareLogo src={settings.logo_url} />
          <span className="hidden sm:block lg:hidden xl:block leading-tight">
            <span className="block font-display text-xl text-[#2d1b0e] group-hover:text-[#991b1b] transition-colors">{settings.site_title}</span>
            <span className="block font-heading text-[9px] tracking-[0.34em] uppercase text-[#5e4a35]">{t("header.subtitle")}</span>
          </span>
        </Link>
        <nav className="hidden lg:flex items-center ml-2">
          {seriesItems.length > 0 && t("nav.chronicles") && <Menu2 label={t("nav.chronicles")} items={seriesItems} testid="nav-chronicles" />}
          {t("nav.codex") && <Menu2 label={t("nav.codex")} items={sectionItems} testid="nav-codex"
            extra={t("nav.all_pages") && <DropdownMenuItem asChild className="font-heading text-sm cursor-pointer rounded-none"><Link to="/codex" data-testid="nav-all-pages">{t("nav.all_pages")}</Link></DropdownMenuItem>} />}
          {t("nav.map") && <NavLink to="/map" data-testid="nav-map" className={navCls}>{t("nav.map")}</NavLink>}
          {settings.vault_enabled && t("nav.vault") && <NavLink to="/vault" data-testid="nav-vault" className={navCls}>{t("nav.vault")}</NavLink>}
          {t("nav.wander") && <WanderButton className={navCls} label={t("nav.wander")} />}
          <MoreMenu label={t("nav.more")} />
        </nav>
        <SearchBox className="ml-auto w-full max-w-[14rem] hidden md:block" />
        <div className="ml-auto md:ml-0 flex items-center gap-2"><SpoilerShieldButton /><AccountLink /></div>
        <div className="lg:hidden"><MobileNav /></div>
      </div>
      <div className="interlace-band" />
    </header>
  );
};
